import { compareEvaluationResults } from "~/utils/evaluation-expected-comparison";
import { withTestDeadline, waitForTestResult, TEST_START_TIMEOUT_MS } from "~/utils/evaluation-test-wait";
import { useEvaluationTestQueue } from "~/stores/useEvaluationTestQueue";
import { getLlmUsageStats } from "~/utils/llm-usage";
import {
  Query,
  Timestamp,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { collectionData } from "rxfire/firestore";
import {
  EvaluationResults,
} from "types/EvaluationResults";
import { EvaluationTest } from "types/EvaluationTest";

export const useEvaluationTestResults = (
  initialEvaluationTest: EvaluationTest
) =>
  defineStore(`/evaluationTest/results/${initialEvaluationTest.id}`, () => {
    const basePath = ref<string>("");

    const expectedResults = ref<EvaluationResults | undefined>(undefined);
    const evaluationTest = ref<EvaluationTest>(initialEvaluationTest);

    const fetchExpectedResults = async (expectedResultsId: string) => {
      const db = useFirestore();
      const expectedResultsRef = doc(
        db,
        `/evaluationTests/${evaluationTest.value.id}/evaluationResults/${expectedResultsId}`
      );

      const expectedResultsSnapshot = await getDoc(expectedResultsRef);
      const expectedResultsData = expectedResultsSnapshot.data();
      if (expectedResultsData) {
        return {
          ...expectedResultsData,
          id: expectedResultsSnapshot.id,
        } as EvaluationResults;
      } else {
        if (basePath.value) {
          const expectedResultsRef = doc(
            db,
            `${basePath.value}/evaluationTests/${evaluationTest.value.id}/evaluationResults/${expectedResultsId}`
          );

          const expectedResultsSnapshot = await getDoc(expectedResultsRef);
          const expectedResultsData = expectedResultsSnapshot.data();
          if (expectedResultsData) {
            return {
              ...expectedResultsData,
              id: expectedResultsSnapshot.id,
            } as EvaluationResults;
          }
        }

        return undefined;
      }
    };

    const testPath = computed(() => {
      return `${basePath.value}/evaluationTests/${evaluationTest.value.id}`;
    });

    const resultsPath = computed(() => {
      return `${testPath.value}/evaluationResults`;
    });

    const streamEvaluationTestResults = () => {
      const db = useFirestore();

      const evaluationTestResultsRef = collection(db, resultsPath.value);
      const evaluationTestResultsQuery = query(
        evaluationTestResultsRef,
        orderBy("createdAt", "desc")
      ) as Query<EvaluationResults>;

      return collectionData<EvaluationResults>(evaluationTestResultsQuery, {
        idField: "id",
      });
    };

    const latestResult = ref<EvaluationResults | undefined>(undefined);

    const fetchLatestResult = async () => {
      const db = useFirestore();

      const evaluationTestResultsRef = collection(db, resultsPath.value);
      const evaluationTestResultsQuery = query(
        evaluationTestResultsRef
      ) as Query<EvaluationResults>;

      const snapshot = await getDocs(evaluationTestResultsQuery);
      var formattedResults = snapshot.docs.map((result) => {
        var createdAt: any = result.data().createdAt;

        // if createdAt is a string convert it to a Timestamp
        if (typeof createdAt === "string") {
          createdAt = Timestamp.fromDate(new Date(createdAt));
        }

        return {
          ...result.data(),
          createdAt: createdAt,
        } as EvaluationResults;
      });

      var sortedResults = formattedResults.sort((a, b) => {
        return b.createdAt.toDate().getTime() - a.createdAt.toDate().getTime();
      });

      if (sortedResults.length == 0) return [] as EvaluationResults[];

      latestResult.value = sortedResults[0];
    };

    const lastCompletedTestTime = ref<number | undefined>(undefined);

    watch(latestResult, (newLatestResult, oldLatestResult) => {
      var wasEvaluating =
        oldLatestResult?.status == EvaluationResultsStatus.evaluating;
      var isComplete =
        newLatestResult?.status == EvaluationResultsStatus.success ||
        newLatestResult?.status == EvaluationResultsStatus.failed;

      if (wasEvaluating && isComplete) {
        lastCompletedTestTime.value = Date.now();
      }

      if (latestResult.value) {
        evaluateCriteriaTree(latestResult.value);
      }
    });

    const isInitialized = ref(false);
    let pendingInitialization: Promise<void> | undefined;
    const initialize = (): Promise<void> => {
      if (isInitialized.value) return Promise.resolve();
      if (pendingInitialization) return pendingInitialization;
      pendingInitialization = (async () => {
        if (evaluationTest.value.expectedResultsId) {
          expectedResults.value = await fetchExpectedResults(evaluationTest.value.expectedResultsId);
        }
        await fetchLatestResult();
        isInitialized.value = true;
      })().finally(() => { pendingInitialization = undefined; });
      return pendingInitialization;
    };

    const setEvaluationExpectedResults = async (
      newExpectedResults: EvaluationResults
    ) => {
      expectedResults.value = newExpectedResults;
      evaluationTest.value.expectedResultsId = newExpectedResults.id;
      const db = useFirestore();
      const docRef = doc(db, `/evaluationTests/${evaluationTest.value.id}`);
      await updateDoc(docRef, { expectedResultsId: newExpectedResults.id });

      if (basePath.value != undefined) {
        const runDocRef = doc(
          db,
          `${basePath.value}/evaluationTests/${evaluationTest.value.id}`
        );
        await updateDoc(runDocRef, {
          expectedResultsId: newExpectedResults.id,
        });

        // Copy the evaluationResults to the parent results document
        const resultsDocRef = doc(
          db,
          `/evaluationTests/${evaluationTest.value.id}/evaluationResults/${newExpectedResults.id}`
        );
        await setDoc(resultsDocRef, newExpectedResults);
      }

      await fetchExpectedResults(newExpectedResults.id);
    };

    const clearEvaluationExpectedResults = async () => {
      evaluationTest.value.expectedResultsId = undefined;
      const db = useFirestore();
      const docRef = doc(db, `/evaluationTests/${evaluationTest.value.id}`);
      await updateDoc(docRef, { expectedResultsId: deleteField() });

      if (basePath.value != undefined) {
        const runDocRef = doc(
          db,
          `${basePath.value}/evaluationTests/${evaluationTest.value.id}`
        );

        await updateDoc(runDocRef, { expectedResultsId: deleteField() });
      }

      expectedResults.value = undefined;
    };

    const hasExpectedResults = computed(() => {
      return expectedResults.value !== undefined;
    });

    const isTheExpectedResults = computed(() => {
      return (evaluationResults: EvaluationResults) => {
        return expectedResults.value?.id === evaluationResults.id;
      };
    });

    const isResultsExpected = computed(() => {
      return (evaluationResults: EvaluationResults) => {
        // Here is where I would compare a particular evaluationResults to expected results
        if (!expectedResults.value) return true;

        return compareEvaluationResults(evaluationResults, expectedResults.value);
      };
    });

    const isRunningTest = ref(false);

    const testQueue = useEvaluationTestQueue();
    let pendingRun: Promise<EvaluationResults> | undefined;

    const runEvaluationTest = (): Promise<EvaluationResults> => {
      // Repeated clicks on the same test share its queued or active request.
      if (pendingRun) return pendingRun;
      isRunningTest.value = true;
      const queuedTestPath = testPath.value;
      const queuedTestId = evaluationTest.value.id;
      pendingRun = (async () => {
        if (
          evaluationTest.value.expectedResultsId &&
          (expectedResults.value == undefined ||
            expectedResults.value.id != evaluationTest.value.expectedResultsId)
        ) {
          expectedResults.value = await fetchExpectedResults(
            evaluationTest.value.expectedResultsId
          );
        }

        // Finish preparation before entering the queue so network starts stay spaced.
        const headers = await useApiHeaders();
        return testQueue.run(async (setStage) => {
          const response = await withTestDeadline((signal) =>
            $fetch<{ path: string; evaluationId: string } | false>(
              `/api/sys-admin/run/test/${queuedTestId}`,
              {
                method: "POST",
                headers,
                signal,
                retry: 0,
                body: { path: queuedTestPath },
              }
            ),
            TEST_START_TIMEOUT_MS,
            "Automark did not acknowledge the test within 60 seconds. It may still be processing it; it has not been retried.",
          );
          if (!response || !response.path || !response.evaluationId) {
            throw new Error("Automark did not start this test. Please try again later.");
          }

          const db = useFirestore();
          const resultsRef = doc(db, `${response.path}/evaluationResults/${response.evaluationId}`);
          setStage("Waiting for the completed result (up to 120 seconds)");
          const result = await waitForTestResult<EvaluationResults>((next, error) =>
            onSnapshot(resultsRef, (snapshot) => next(snapshot.data() as EvaluationResults | undefined), error)
          );
          latestResult.value = result;
          return result;
        }, evaluationTest.value.documentText ?? evaluationTest.value.name ?? queuedTestId);

      })().finally(() => {
        isRunningTest.value = false;
        pendingRun = undefined;
      });
      return pendingRun;
    };

    const setBasePath = (path: string) => {
      basePath.value = path;
    };

    const llmUsageStats = computed(() =>
      latestResult.value ? getLlmUsageStats(latestResult.value) : undefined
    );

    const isOpen = ref(false);

    return {
      initialize,
      setEvaluationExpectedResults,
      clearEvaluationExpectedResults,
      streamEvaluationTestResults,
      runEvaluationTest,
      latestResult,
      hasExpectedResults,
      isTheExpectedResults,
      isResultsExpected,
      expectedResults,
      isRunningTest,
      setBasePath,
      basePath,
      lastCompletedTestTime,
      llmUsageStats,
      isOpen,
    };
  });
